import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const huddingePages: ServicePageConfig[] = [
  {
    slug: "bortforsling-huddinge",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Huddinge – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Huddinge. Vi hämtar möbler, vitvaror och skräp i Flemingsberg, Huddinge centrum, Stuvsta, Skogås, Trångsund och Vårby. Fast pris, ofta samma dag.",
      h1: "Bortforsling Huddinge",
    },

    hero: {
      heading: "Bortforsling i Huddinge",
      intro:
        "Boka bortforsling i Huddinge – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Flemingsberg och Huddinge centrum till villakvarteren i Snättringe, Segeltorp och Stuvsta. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Huddinge – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Huddinge",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Huddinge och körs till SRV:s anläggning i Gladö",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Snättringe, Huddinge",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Flemingsberg, Huddinge",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Huddinge centrum",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Skogås, Huddinge",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Vårby, Huddinge",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Huddinge – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Huddinge är en av Stockholms största kommuner och rymmer allt från de tätbebyggda kvarteren i Flemingsberg och Huddinge centrum till villaområdena i Snättringe, Fullersta, Segeltorp och Vistaberg. Den blandningen gör bortforsling till olika utmaningar beroende på var du bor: i flerbostadshusen kring Sjödalen och Vårby gård handlar det ofta om trånga trapphus och hissar, medan det i villaområdena snarare handlar om ett fullt garage eller ett källarförråd som ska tömmas. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Huddinge har en stor fördel här – kommunens egna återvinningsbolag SRV återvinning har sin huvudanläggning, Sofielund vid Gladö kvarn, mitt i kommunen, och det finns dessutom en återvinningscentral i Skogås. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E4/E20 passerar i väster vid Vårby och Masmo, och att Huddingevägen och Glömstavägen binder ihop kommunen, tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats i Huddinge redan samma dag du bokar. Våra medarbetare är vana vid både de smala villagatorna i Stuvsta och Snättringe och de större bostadsgårdarna i Flemingsberg och Skogås – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Huddinge",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Snättringe, Segeltorp eller Vistaberg är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Flemingsberg, Huddinge centrum eller Vårby gård, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning. Det sparar tid jämfört med att leta plats på gatan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Huddingebo på SRV:s återvinningscentraler, till exempel i Skogås.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Huddinge",
          content:
            "Vi utför bortforsling i hela Huddinge kommun – från de regionala stadskärnorna till de etablerade villakvarteren. Oavsett om du bor i en lägenhet i Flemingsberg, en villa i Snättringe, ett radhus i Vistaberg eller en etta vid Huddinge centrum kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna – Huddinge centrum, Sjödalen och Fullersta – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära pendeltågsstationen och bussterminalen. I Flemingsberg, kommunens snabbt växande stadskärna med Karolinska, Södertörns högskola och nya bostadskvarter, hanterar vi både studentboenden och nybyggda lägenheter. I norr når vi villaområdena i Stuvsta, Snättringe, Segeltorp och Glömsta utan problem.\n\nVi är minst lika snabba i kommunens södra och västra delar: Skogås och Trångsund vid sjön Drevviken, samt Vårby gård och Masmo längs tunnelbanans röda linje. Tack vare närheten till E4/E20, Huddingevägen, Nynäsvägen och kommunens pendeltågsstationer – Stuvsta, Huddinge och Flemingsberg – planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Huddinge",
          content:
            "En viktig del av vår bortforsling i Huddinge är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Huddinge är hemkommun för SRV återvinning – som ägs gemensamt av Huddinge, Haninge, Nynäshamn, Botkyrka och Salem – och de anläggningar vi använder mest för uppdrag i kommunen är:\n\nSofielunds återvinningsanläggning vid Gladö kvarn – SRV:s stora huvudanläggning, belägen mitt i Huddinge och en av Sveriges största till ytan. Skogås återvinningscentral – smidig för uppdrag i de södra och östra delarna kring Skogås och Trångsund. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Huddinge med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Huddinges kommundelar, från villagatorna i Stuvsta och Snättringe till bostadsgårdarna i Flemingsberg och Skogås, och vet att SRV:s anläggningar i Gladö och Skogås ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E4/E20 passerar i västra Huddinge. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Huddinge, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via SRV:s återvinningscentraler. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Huddinge?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Huddinge.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Huddinge?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Huddinge?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E4/E20 passerar i västra Huddinge, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Huddinge?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Huddinge?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Huddinge centrum, Flemingsberg och Vårby gård. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Huddinge?",
        answer:
          "Ja, en stor del av våra uppdrag i Huddinge är villahämtningar i Snättringe, Segeltorp, Stuvsta och Vistaberg. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Huddinge försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Huddinge?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Huddinge är hemkommun för SRV återvinning, så det blir oftast Sofielund vid Gladö kvarn eller återvinningscentralen i Skogås. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Huddinge på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Huddinge?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Snättringe och lägenhetsförråden i Flemingsberg och Huddinge centrum.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Huddinge?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet vid Huddinge centrum och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Huddinge täcker ni?",
        answer:
          "Vi utför bortforsling i hela Huddinge – Flemingsberg, Huddinge centrum, Sjödalen, Fullersta, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård, Masmo och övriga områden. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Huddinge?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Huddingebo på SRV:s återvinningscentraler, till exempel i Skogås.",
      },
      {
        question: "Vilka kommuner runt Huddinge utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Huddinge i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Huddinge kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Huddinge idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Huddinge kommun.",
    },

    en: {
      slug: "junk-removal-huddinge",
      meta: {
        title: "Junk Removal Huddinge – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Huddinge. We pick up furniture, appliances and bulky items in Flemingsberg, Huddinge centrum, Stuvsta, Skogås, Trångsund and Vårby. Fixed price, often same day.",
        h1: "Junk Removal Huddinge",
      },
      hero: {
        heading: "Junk Removal in Huddinge",
        intro:
          "Book junk removal in Huddinge – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Flemingsberg and Huddinge centrum to the villa neighbourhoods of Snättringe, Segeltorp and Stuvsta. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Huddinge – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Huddinge",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Huddinge and driven to SRV's Gladö facility",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Snättringe, Huddinge",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Flemingsberg, Huddinge",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Huddinge centrum",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Skogås, Huddinge",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Vårby, Huddinge",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Huddinge – fast, hassle-free and responsible",
        mainText:
          "Huddinge is one of Stockholm's largest municipalities and contains everything from the densely built blocks of Flemingsberg and Huddinge centrum to the villa areas of Snättringe, Fullersta, Segeltorp and Vistaberg. That mix makes junk removal a different challenge depending on where you live: in the apartment buildings around Sjödalen and Vårby gård it is often narrow stairwells and elevators, while in the villa areas it is more about a full garage or a basement storage room to be cleared. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Huddinge has a big advantage here – the municipality's own recycling company, SRV återvinning, has its main facility, Sofielund at Gladö kvarn, right in the middle of the municipality, and there is also a recycling centre in Skogås. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E4/E20 motorway passes in the west at Vårby and Masmo, and Huddingevägen and Glömstavägen tie the municipality together, we move quickly between districts. This means we can often be on site in Huddinge the same day you book. Our workers are used to both the narrow villa streets of Stuvsta and Snättringe and the larger residential courtyards of Flemingsberg and Skogås – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Huddinge",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Snättringe, Segeltorp or Vistaberg, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Flemingsberg, Huddinge centrum or Vårby gård, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use. It saves time compared to searching for a spot on the street.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Huddinge resident you can drop those off for free at SRV's recycling centres, such as the one in Skogås.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Huddinge",
            content:
              "We perform junk removal across the entire municipality of Huddinge – from the regional urban cores to the established villa neighbourhoods. Whether you live in an apartment in Flemingsberg, a villa in Snättringe, a townhouse in Vistaberg or a studio near Huddinge centrum, we can be on site, often the same day.\n\nIn the central parts – Huddinge centrum, Sjödalen and Fullersta – we are highly experienced with apartment pickups via elevator and stairwell, close to the commuter-rail station and bus terminal. In Flemingsberg, the municipality's fast-growing urban core with Karolinska, Södertörn University and new residential blocks, we handle both student housing and newly built apartments. To the north we reach the villa areas of Stuvsta, Snättringe, Segeltorp and Glömsta without issue.\n\nWe are just as quick in the southern and western parts of the municipality: Skogås and Trångsund by Lake Drevviken, and Vårby gård and Masmo along the red metro line. Thanks to the proximity of the E4/E20, Huddingevägen, Nynäsvägen and the municipality's commuter-rail stations – Stuvsta, Huddinge and Flemingsberg – we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Huddinge",
            content:
              "An important part of our junk removal in Huddinge is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Huddinge is the home municipality of SRV återvinning – jointly owned by Huddinge, Haninge, Nynäshamn, Botkyrka and Salem – and the facilities we use most for jobs in the municipality are:\n\nSofielund recycling facility at Gladö kvarn – SRV's large main facility, located in the middle of Huddinge and one of the biggest in Sweden by area. Skogås recycling centre – convenient for jobs in the southern and eastern parts around Skogås and Trångsund. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Huddinge with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Huddinge's districts, from the villa streets of Stuvsta and Snättringe to the residential courtyards of Flemingsberg and Skogås, and that SRV's facilities at Gladö and Skogås are close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E4/E20 passes through western Huddinge. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Huddinge, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via SRV's recycling centres. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Huddinge cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Huddinge.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Huddinge?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Huddinge?",
          answer:
            "We have vans and staff across the Stockholm region and the E4/E20 passes through western Huddinge, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Huddinge?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Huddinge?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Huddinge centrum, Flemingsberg and Vårby gård. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Huddinge?",
          answer:
            "Yes, a large share of our jobs in Huddinge are villa pickups in Snättringe, Segeltorp, Stuvsta and Vistaberg. We reverse onto the driveway where possible and clear garages, basements and storage units. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Huddinge insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Huddinge?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Huddinge is the home municipality of SRV återvinning, so it is usually Sofielund at Gladö kvarn or the recycling centre in Skogås. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Huddinge on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Huddinge?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to both the villa storage rooms in Snättringe and the apartment storage units in Flemingsberg and Huddinge centrum.",
        },
        {
          question: "Can you make multiple stops during junk removal in Huddinge?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment near Huddinge centrum and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Huddinge do you cover?",
          answer:
            "We perform junk removal across all of Huddinge – Flemingsberg, Huddinge centrum, Sjödalen, Fullersta, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård, Masmo and all other areas. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Huddinge?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Huddinge resident you can drop those off for free at SRV's recycling centres, such as the one in Skogås.",
        },
        {
          question: "Which municipalities around Huddinge do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Huddinge in 3 easy steps",
        step2Text:
          "Our young workers in Huddinge come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Huddinge today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Huddinge.",
      },
    },
  },
];
